import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  input,
  signal,
} from '@angular/core';
import { TeammateAvatarComponent } from './teammate-avatar.component';
import {
  PRESENTATION_TO_AVATAR_VARIANT,
  TeammateAvatarCropContext,
  TeammatePresentation,
  TeammateVisual,
} from './teammate-avatar.config';
import { TeammateIconComponent } from './teammate-icon.component';

/**
 * The SB-3519 visual seam (#1255) — the single place that decides which of a
 * teammate's two visuals to draw.
 *
 * Eight render sites, one wrapper: no page, card, row or dropdown writes its
 * own check on the visual type. That is the whole contract. If you find
 * yourself branching on the type at a call site, the branch belongs here.
 *
 * ## Why the input is `presentation` and not `variant`
 *
 * `variant` is the avatar's size-and-crop axis, and it cannot express what the
 * icon needs. Three surfaces share `small` — the nav row, the admin Teammates
 * list and the Builder's intro tile — and `card` is not a size at all but the
 * uncropped full-body form that the avatar branches its whole structure on.
 * Naming the surface instead lets the treatment be resolved once, here, rather
 * than having T6 (#1257) and T7 (#1258) each reach into the icon component and
 * add their own discriminator.
 *
 * Do not merge two presentations because their treatments happen to match. The
 * axis is not justified by a value difference — those come and go, and
 * `nav-slot` and `admin-row` will be identical again once T7 lands — but by
 * there being somewhere for a per-surface treatment to hang at all. `variant`
 * offers no such place.
 *
 * ## The host class is load-bearing
 *
 * `TeammateAvatarComponent` declares `block h-full w-full` (:85-87) and every
 * one of the eight sites depends on it — six drop the visual as the sole child
 * of a fixed box, two put sizing on the element itself. Inserting this wrapper
 * into that chain without repeating the class gives the parent an inline child
 * of zero height. No spec in this ticket computes geometry, so that failure is
 * visible only in a browser.
 */
@Component({
  selector: 'squadbox-teammate-visual',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: 'block h-full w-full',
  },
  imports: [TeammateAvatarComponent, TeammateIconComponent],
  template: `
    @let resolved = visual();

    @if (resolved.type === 'icon') {
      <squadbox-teammate-icon
        [icon]="resolved.icon"
        [baseColor]="resolved.baseColor"
        [darkColor]="resolved.darkColor"
        [presentation]="presentation()"
        [transparentBackground]="transparentBackground()"
        [hovered]="hovered()"
        [active]="active()"
      />
    } @else if (avatarVisible()) {
      <!--
        The visual object is passed straight through rather than narrowed into
        a fresh object. The extra discriminator property is harmless on a
        non-literal, and passing the same reference is what keeps the avatar's
        init effect from re-running every change detection pass — which would
        reset any in-flight hover animation. Narrowing here would re-arm that
        bug INSIDE the seam, where the per-site reference-identity specs
        cannot see it.
      -->
      <squadbox-teammate-avatar
        [class]="avatarClass()"
        [avatar]="resolved"
        [variant]="avatarVariant()"
        [transparentBackground]="transparentBackground()"
        [hovered]="hovered()"
        [active]="active()"
        [looping]="looping()"
      />
    } @else {
      <div [class]="placeholderClass()"></div>
    }
  `,
})
export class TeammateVisualComponent {
  public readonly visual = input.required<TeammateVisual>();
  public readonly presentation = input.required<TeammatePresentation>();

  // Pass-through inputs. Defaults mirror TeammateAvatarComponent's own, so no site
  // changes behavior by omitting one. `looping` is the only one inert on the icon
  // arm: `transparentBackground` is honoured by card-header, and since T6 (#1257)
  // `hovered` / `active` drive the highlight on the presentations that drop their
  // base color — the icon arm follows the same rule the default visual does.
  public readonly transparentBackground = input<boolean>(false);
  public readonly hovered = input<boolean | undefined>(undefined);
  public readonly active = input<boolean>(false);
  /**
   * Forwarded for completeness and currently reaching no caller: the only
   * consumer of the avatar's looping mode is `app-loading-screen.component.ts`,
   * which reads the presets directly and stays outside this seam permanently.
   * Stated so nobody debugging a non-looping icon goes looking in the icon arm.
   */
  public readonly looping = input<boolean>(false);

  /**
   * Delay before the avatar arm paints, in milliseconds. Two sites use it —
   * the nav row (100) and the Builder banner (150) — because the avatar's GSAP
   * initialization blocks first paint and would stall an animation the site is
   * running. The icon arm has no GSAP, so it never waits.
   *
   * This replaces the `@defer (on timer(...))` blocks those two sites carried.
   * Neither was ever a lazy chunk — `app.routes.ts:17` statically imports the
   * teammates barrel, so the avatar and GSAP are already in the eager graph,
   * and the timer was always a render delay. Keeping `@defer` would have cost
   * three copies of the avatar arm, since its duration must be a literal, and
   * would have stranded the nav's fade-in class on the placeholder instead of
   * on the element that appears.
   */
  public readonly avatarDelayMs = input<number>(0);
  /** Applied to the avatar element itself, so an appearance animation runs on it. */
  public readonly avatarClass = input<string>('');
  /** The site's own placeholder markup while the avatar arm is waiting. */
  public readonly placeholderClass = input<string>('h-full w-full');

  private readonly avatarReady = signal(false);
  protected readonly avatarVisible = computed(
    () => this.avatarDelayMs() <= 0 || this.avatarReady(),
  );

  /**
   * The six named surfaces reduce onto the avatar's four size variants.
   *
   * The mapping itself moved to `PRESENTATION_TO_AVATAR_VARIANT` in the config
   * when T6 (#1257) needed to read it a second time: the icon arm's highlight
   * rule is the avatar's, and the avatar keys that rule on the VARIANT. A
   * `switch` here plus a hand-copied variant set there would be one fact in two
   * files, and remapping a presentation would silently put the two arms out of sync.
   * Two presentations sharing a variant can still differ in container — that is
   * still the point of the `presentation` axis, and it is unaffected.
   */
  protected readonly avatarVariant = computed<
    'card' | TeammateAvatarCropContext
  >(() => PRESENTATION_TO_AVATAR_VARIANT[this.presentation()]);

  constructor() {
    effect((onCleanup) => {
      const delay = this.avatarDelayMs();
      // Reset before re-arming. Without this, a delay raised AFTER the first
      // paint (100 to 200, say) would not wait again: `avatarReady` would still
      // be true from the previous run and `avatarVisible` would short-circuit.
      // No caller changes the value today — both pass a literal — so nothing
      // exercises it, which is exactly why it is worth one line now rather than
      // a puzzle for whoever adds the third deferring surface.
      this.avatarReady.set(false);
      if (delay <= 0) {
        return;
      }
      const timer = setTimeout(() => this.avatarReady.set(true), delay);
      onCleanup(() => clearTimeout(timer));
    });
  }
}
